import { useEffect, useId, useState } from 'react';
import { Link } from 'react-router-dom';
import { LuBell } from 'react-icons/lu';

import { NotificationsPanel } from 'components/notifications-panel';
import { useAuth } from 'context/AuthContext';
import { NotificationRow, familyAccent, rowText, rowTitle } from 'pages/Notificacoes/notification-inbox';
import { getNotificationInbox } from 'services/notificationInbox.service';
import { FOCO_VISIVEL } from 'utils/foco';

import { SectionHeading } from './section-heading';

/**
 * Faixa "Acompanhe suas notificações" da Home (HOME-40 a HOME-42): o aviso não lido mais recente.
 *
 * Uma leitura só, `get_notification_inbox` com `{ status: 'unread', limit: 1 }`: o `$match` casa o
 * índice {player, status, created} e o `$sort` sai dele, então custa o mesmo que o contador que
 * havia aqui — e a resposta vazia já diz que não há aviso. Sem aviso não lido, a seção some.
 *
 * "Ver todas as notificações" abre o painel lateral do design (`NotificationsPanel`). Abrir a faixa
 * não marca o aviso como lido; quem marca é o painel, ao clicar no aviso. Ao fechar o painel o aviso
 * é relido, porque o que estava aqui pode ter sido lido lá.
 */
export function NotificationsRow({ waitingRefresh = false }: { waitingRefresh?: boolean }) {
  // `userRevision` sobe a cada refreshUser concluído (ex.: websocket de notificação), e só então
  // o aviso é relido (DCE-25). Com `waitingRefresh` (volta com `direction: 'back'`), a leitura
  // da montagem espera esse refresh terminar, para sair uma vez só (DCE-57).
  const { userRevision } = useAuth();
  const [latest, setLatest] = useState<NotificationRow | null>(null);
  const [panelOpen, setPanelOpen] = useState(false);
  const [panelClosings, setPanelClosings] = useState(0);
  const headingId = useId();

  useEffect(() => {
    if (waitingRefresh) return;

    let cancelled = false;

    getNotificationInbox({ status: 'unread', limit: 1 })
      .then(rows => {
        if (!cancelled) setLatest(rows[0] ?? null);
      })
      .catch(() => {
        if (!cancelled) setLatest(null);
      });

    return () => {
      cancelled = true;
    };
  }, [userRevision, waitingRefresh, panelClosings]);

  function closePanel() {
    setPanelOpen(false);
    setPanelClosings(count => count + 1);
  }

  const panel = <NotificationsPanel isOpen={panelOpen} onClose={closePanel} />;

  // O painel continua montado quando o último aviso não lido some, para fechar normalmente.
  if (!latest) return panel;

  const text = rowText(latest);

  return (
    <section aria-labelledby={headingId} className="flex flex-col gap-2.5">
      <SectionHeading id={headingId}>Acompanhe suas notificações</SectionHeading>

      {/* D3: 34px não tem degrau no Tailwind, e min-w não tem escala de spacing no 3.3. */}
      {/* A cor do assunto (a da faixa do e-mail, NOTIF-20) é o fundo do cartão, como nos cartões do
          painel: como cor de texto sobre o branco, nenhuma das quatro passa nem no mínimo de texto
          grande (3:1). Sobre elas, o `brand-blue-dark` dá de 4,81:1 a 5,87:1. */}
      <div
        data-testid="preview-da-notificacao"
        className={`flex flex-wrap items-center gap-3.5 rounded-jj-lg px-5 py-4 text-content shadow-jj-01 sm:px-[34px] ${familyAccent(latest.family)}`}
      >
        <p className="font-display text-2xl font-bold leading-tight text-brand-blue-dark">{rowTitle(latest)}</p>

        <p className="min-w-[240px] flex-1 text-jj-body-sm font-semibold leading-normal">
          {text && `${text} `}
          <CheckLink url={latest.inapp?.cta_url} onOpenPanel={() => setPanelOpen(true)} />
        </p>

        <button
          type="button"
          aria-haspopup="dialog"
          onClick={() => setPanelOpen(true)}
          className={`flex cursor-pointer items-center gap-2 whitespace-nowrap border-none bg-transparent p-0 font-text text-jj-label font-bold uppercase tracking-[.03em] text-brand-blue-dark hover:underline ${FOCO_VISIVEL}`}
        >
          <LuBell size={15} aria-hidden="true" />
          Ver todas as notificações
        </button>
      </div>

      {panel}
    </section>
  );
}

/** "Confira aqui!": o destino do aviso, externo em nova aba; sem destino, o painel de notificações. */
function CheckLink({ url, onOpenPanel }: { url?: string; onOpenPanel: () => void }) {
  // `brand-blue` sobre as cores do assunto fica abaixo de 4,5:1; o `brand-blue-dark` passa nas quatro.
  const className = 'text-brand-blue-dark underline decoration-2 hover:decoration-[3px]';

  if (url && /^https?:\/\//.test(url)) {
    return (
      <a href={url} target="_blank" rel="noreferrer" className={className}>
        Confira aqui!
      </a>
    );
  }

  if (!url) {
    return (
      <button
        type="button"
        aria-haspopup="dialog"
        onClick={onOpenPanel}
        className={`cursor-pointer border-none bg-transparent p-0 font-semibold ${className} ${FOCO_VISIVEL}`}
      >
        Confira aqui!
      </button>
    );
  }

  return (
    <Link to={url} className={className}>
      Confira aqui!
    </Link>
  );
}
